import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

export type DebtReminderSettings = {
  workshop_name: string;
  template: string;
  extension_days: number | null;
  preview: string;
};

const SAMPLE_CLIENT = "Aziz mijoz";
const SAMPLE_DEBT = "125 000";
const DEFAULT_TEMPLATE =
  "Assalomu alaykum, {client_name}. Joriy qarzingiz: {debt_amount} so'm.";

// Keep this composition in lockstep with debt_reminder_service.render_message:
// Telegram receives the server-owned workshop identity, a blank line, then the
// owner's rendered template (unless the template already contains that name).
export function composeDebtReminderPreview(settings: DebtReminderSettings) {
  const source = settings.template.trim() || DEFAULT_TEMPLATE;
  const body = source
    .replaceAll("{client_name}", SAMPLE_CLIENT)
    .replaceAll("{debt_amount}", SAMPLE_DEBT);
  return body.includes(settings.workshop_name)
    ? body
    : `${settings.workshop_name}\n\n${body}`;
}

type Props = {
  open: boolean;
  settings: DebtReminderSettings | null;
  onOpenChange: (open: boolean) => void;
  onChange: (settings: DebtReminderSettings) => void;
  onSave: () => void | Promise<void>;
};

const VARIABLES = ["client_name", "debt_amount"] as const;

export function DebtReminderSettingsDialog({
  open,
  settings,
  onOpenChange,
  onChange,
  onSave,
}: Props) {
  const { t } = useTranslation(["qarzlar", "common"]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function insertVariable(variable: (typeof VARIABLES)[number]) {
    if (!settings) return;
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? settings.template.length;
    const end = textarea?.selectionEnd ?? start;
    const token = `{${variable}}`;
    onChange({
      ...settings,
      template:
        settings.template.slice(0, start) + token + settings.template.slice(end),
    });
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + token.length, start + token.length);
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>{t("reminder.settings")}</DialogTitle>
        <DialogDescription>{t("reminder.settingsHint")}</DialogDescription>
      </DialogHeader>
      {settings && (
        <>
          <DialogBody className="space-y-3">
            <label className="block space-y-1">
              <span className="text-sm font-medium">{t("reminder.template")}</span>
              <Textarea
                ref={textareaRef}
                value={settings.template}
                onChange={(event) =>
                  onChange({ ...settings, template: event.target.value })
                }
              />
            </label>

            <div className="flex flex-wrap gap-1.5" aria-label={t("reminder.variables")}>
              {VARIABLES.map((variable) => (
                <Button
                  key={variable}
                  variant="outline"
                  size="sm"
                  className="h-auto gap-1.5 px-2 py-1 font-normal"
                  onClick={() => insertVariable(variable)}
                >
                  <code className="text-xs font-medium">{`{${variable}}`}</code>
                  <span className="text-[11px] text-muted-foreground">
                    {t(`reminder.variableLabels.${variable}`)}
                  </span>
                </Button>
              ))}
            </div>

            <p className="text-xs text-muted-foreground">
              {t("reminder.workshopNameHint")}
            </p>

            <label className="block space-y-1">
              <span className="text-sm font-medium">{t("reminder.extension")}</span>
              <Input
                type="number"
                min={1}
                value={settings.extension_days ?? ""}
                onChange={(event) =>
                  onChange({
                    ...settings,
                    extension_days: event.target.value
                      ? Number(event.target.value)
                      : null,
                  })
                }
              />
            </label>

            <div className="rounded-md border bg-muted/60 px-3 py-2 text-sm">
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                {t("reminder.preview")}
              </div>
              <div className="whitespace-pre-wrap">
                {composeDebtReminderPreview(settings)}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => void onSave()}>{t("form.save")}</Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
